import { Suspense, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { SESSION_QUERY_KEY, getSession, useSession } from "@/lib/auth";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import {
  WALLED_SESSION_POLL_MS,
  keptPageUnderWall,
  planWallCovers,
} from "@/lib/planWall";

const PlanWallPage = lazyWithRetry(
  () => import("@/routes/admin/billing/PlanWallPage"),
);

/**
 * Route-level plan wall (FEAT-34), nested INSIDE `RequireAuth` — so a session
 * exists by the time this renders. It is the admin subtree's one Suspense
 * boundary as well (every page below is lazy), which is why it replaces
 * `SuspendedOutlet` there rather than nesting inside it.
 *
 * While the workspace must pick a plan, every admin page but Billing is
 * replaced by the wall and never MOUNTED: the server answers 402
 * `PLAN_REQUIRED` to its reads now, so mounting it would only fire requests
 * that are refused (twice — queries retry once) behind a page nobody sees.
 *
 * And when the wall lifts — the customer just paid — every cached query is
 * invalidated once. A page that was open when the trial ran out holds queries
 * that errored with that 402; without this, the wall would drop to reveal
 * "couldn't load" cards on the very screen that should say thank you.
 *
 * And it has to NOTICE the wall lifting: a plan granted by an operator, or
 * bought in another tab, changes nothing in this tab's session for up to
 * `useSession`'s 5-minute staleTime (focus refetch is off app-wide). So while
 * walled — and only then — one extra observer re-asks every
 * `WALLED_SESSION_POLL_MS` and on window focus (FEAT-34 verify, 2026-10-06:
 * the wall stayed up after a grant until a reload).
 *
 * UX only, like every guard in this tree: `entitlements.enforce_plan_wall` is
 * the boundary.
 */
export function RequirePlan() {
  const { data: session } = useSession();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const walled = Boolean(session?.needs_plan);

  useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: getSession,
    enabled: walled,
    refetchInterval: WALLED_SESSION_POLL_MS,
    refetchOnWindowFocus: "always",
    staleTime: 5 * 60_000,
    retry: false,
  });

  // The render on which the wall lifts shows the bare fallback, not the page:
  // the page's queries still hold the 402s they met behind the wall, and an
  // invalidate keeps `isError` until each refetch lands — the page flashed
  // "Couldn't load …" on the very screen that should say thank you (FEAT-34
  // verify, 2026-10-06). So the errored ones are RESET (pending, no error)
  // first, everything else is invalidated, and then the page mounts.
  const wasWalled = useRef(walled);
  const [, setLifted] = useState(0);
  const lifting = wasWalled.current && !walled;
  // R8 (pre-ship 2026-10-07): the page a wall rose UNDER stays mounted —
  // swapping it for <PlanWallPage> unmounted an open editor and threw its
  // unsaved work away. Its own AdminLayout hides it behind the wall instead.
  // Computed during render (a ref, idempotent) because the swap would happen
  // on this very render.
  const keptPage = useRef<string | null>(null);
  keptPage.current = keptPageUnderWall(keptPage.current, {
    walled,
    wasWalled: wasWalled.current,
    pathname,
  });
  const keep = keptPage.current !== null;
  useEffect(() => {
    if (!wasWalled.current && walled) {
      // The wall rose under an open page: the click that met the 402 has
      // just toasted "Couldn't …" — the wall is the explanation, so the
      // toast goes (FEAT-34 verify, 2026-10-06).
      toast.dismiss();
    }
    if (wasWalled.current && !walled) {
      void queryClient.resetQueries({
        predicate: (query) => query.state.status === "error",
      });
      void queryClient.invalidateQueries();
      wasWalled.current = false;
      keptPage.current = null;
      setLifted((n) => n + 1);
      return;
    }
    wasWalled.current = walled;
  }, [walled, queryClient]);

  const fallback = <div className="min-h-screen bg-background" />;
  return (
    <Suspense fallback={fallback}>
      {lifting && !keep
        ? fallback
        : walled && planWallCovers(pathname) && !keep
          ? <PlanWallPage />
          : <Outlet />}
    </Suspense>
  );
}
